// src/components/CriteriaHelpModal.jsx
import React, { useState, useEffect } from 'react';

// Court vertical réaliste : ratio ~2.9 (longueur/largeur)
// ViewBox : 180 large x 320 haut
const W = 144;
const H = 256;
const ML = 20;  // marge latérale
const MT = 12;  // marge top/bottom
// Court simple (lignes de simple)
const CL = ML + 22;  // left singles line
const CR = W - ML - 22; // right singles line
const NET_Y = H / 2;  // filet au milieu

// Lignes de service : 6.4m depuis le filet sur 23.77m total = ~27% depuis le filet
const SERV_OFFSET = Math.round((H/2 - MT) * 0.538); // 6.4m/11.885m = 53.8% de la demi-longueur
const SERV_TOP = NET_Y - SERV_OFFSET;
const SERV_BOT = NET_Y + SERV_OFFSET;

// Positions joueurs
const J1_START = { x: W/2 + 18, y: H - 34 };  // centre-droit (côté coup droit)
const J1_MOVED = { x: W/2 - 35, y: H - 40 };   // déplacé vers la gauche (côté revers)

// Balle : arrive de la zone adverse côté gauche J1 = côté droit du court (droite pour J2 = gauche pour J1 vue de derrière)
const BALL_FROM  = { x: CR - 10, y: MT + 30 };  // depuis haut-droite adverse
const BALL_LAND  = { x: CL + 15, y: H - 70 };   // atterrit côté revers J1 (gauche bas)

// Cibles dans le camp adverse (vu de derrière J1) :
// Côté GAUCHE adverse = x petit (CL côté)
// Côté DROIT adverse  = x grand (CR côté)
// Inside-Out : J1 vise côté GAUCHE adverse
// Inside-In  : J1 vise côté DROIT adverse (long de ligne, même côté que J1 est parti)
// Inside-Middle : centre
const ASTUCES = {
  label: 'Astuces',
  color: '#F57C00',
  items: [
    "Les familles et sous-types sont dynamiques : choisissez d'abord une famille, puis précisez le sous-type du coup associé.",
    "Sur un service gagnant ou un retour gagnant, le nombre de frappes est masqué car déterminé automatiquement.",
    "Le nombre de frappes correspond à chaque coup joué par les 2 joueurs (≠ échange). Exemple : un coup droit gagnant après le service = 3-4 frappes (Service + Retour + Coup droit).",
    "Sélectionnez au moins la famille et un résultat (Point ou Faute) pour débloquer les boutons d'ajout d'un point.",
    "2 façons d'ajouter un point : soit un coup gagnant du joueur, soit une faute de son adversaire.",
    "La famille ??? permet d'entrer un point libre quand on ne sait pas codifier un point, ou pour rattraper un score manqué. Les points libres sont exclus de certaines statistiques pour garder leur cohérence.",
    "Le bouton + affiche des critères complémentaires optionnels : réussite au service (1er, 2nd, DF), zones et/ou effets. Vous pouvez les combiner librement.",
    "Astuce service : si vous utilisez réussite au service + zones, sélectionnez ces critères en premier — le système auto-complétera la famille et le sous-type pour éviter la double saisie.",
  ]
};

const SHOTS = {
  'inside-out': {
    label: 'Inside-Out',
    color: '#1976D2',
    desc: `La balle arrive côté revers. J1 se déplace à gauche s'il est droitier et à droite s'il est gaucher et frappe un coup droit vers le côté gauche de l'adversaire.
    Il fait un coup droit de décalage décroisé`,
    target: { x: CR - 12, y: MT + 22 },
  },
  'inside-middle': {
    label: 'Inside-Middle',
    color: '#7B1FA2',
    desc: `La balle arrive côté revers. J1 se déplace à gauche s'il est droitier et à droite s'il est gaucher et frappe un coup droit vers le centre du court de l'adversaire.
    Il fait un coup droit de décalage au centre`,
    target: { x: W/2, y: MT + 20 },
  },
  'inside-in': {
    label: 'Inside-In',
    color: '#388E3C',
    desc: `La balle arrive côté revers. J1 se déplace à gauche s'il est droitier et à droite s'il est gaucher et frappe un coup droit vers le côté droit de l'adversaire.
    Il fait un coup droit de décalage long de ligne`,
    target: { x: CL + 12, y: MT + 22 },
  },
};

const STEP_LABELS = [
  '▶ Appuie sur Démarrer',
  'Étape 1 — La balle arrive côté revers de J1',
  'Étape 2 — J1 se déplace vers la gauche (position coup droit)',
  {
    'inside-out':    `Étape 3 — Coup droit décroisé côté gauche de l'adversaire`,
    'inside-middle': 'Étape 3 — Coup droit vers le centre',
    'inside-in':     `Étape 3 — Coup droit croisé (long de ligne) côté droit de l'adversaire`,
  },
];

export default function CriteriaHelpModal({ onClose }) {
  const [selected, setSelected] = useState('inside-out');
  const [step, setStep] = useState(0);
  const shot = SHOTS[selected] || SHOTS['inside-out'];

  useEffect(() => { setStep(0); }, [selected]);
  const nextStep = () => { if (step < 3) setStep(s => s + 1); };

  const label = step < 3 ? STEP_LABELS[step] : STEP_LABELS[3][selected];

  const ballPos  = step === 0 ? null : step <= 2 ? BALL_LAND : shot.target;
  const playerX  = step >= 2 ? J1_MOVED.x : J1_START.x;
  const playerY  = step >= 2 ? J1_MOVED.y : J1_START.y;
  const dx = playerX - J1_START.x;
  const dy = playerY - J1_START.y;

  return (
    <div onClick={onClose} style={{
      position:'fixed',top:0,left:0,right:0,bottom:0,
      backgroundColor:'rgba(0,0,0,0.6)',zIndex:2000,
      display:'flex',alignItems:'center',justifyContent:'center',padding:'12px',
    }}>
      <div onClick={e=>e.stopPropagation()} style={{
        backgroundColor:'white',borderRadius:'16px',padding:'14px',
        maxWidth:'320px',width:'100%',boxShadow:'0 8px 32px rgba(0,0,0,0.25)',
      }}>

        {/* Header */}
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:'10px'}}>
          <h3 style={{margin:0,fontSize:'14px',fontWeight:'700',color:'#1a1a2e'}}>Aide aux critères</h3>
          <button onClick={onClose} style={{border:'none',background:'none',fontSize:'18px',cursor:'pointer',color:'#999'}}>✕</button>
        </div>

        {/* Onglets */}
        <div style={{display:'flex',gap:'5px',marginBottom:'10px'}}>
          {Object.entries(SHOTS).map(([key,val])=>(
            <button key={key} onClick={()=>setSelected(key)} style={{
              flex:1,padding:'7px 2px',border:'none',borderRadius:'8px',
              fontSize:'10px',fontWeight:'600',cursor:'pointer',
              backgroundColor:selected===key ? val.color : '#f0f0f0',
              color:selected===key ? 'white' : '#555',
            }}>{val.label}</button>
          ))}
          <button onClick={()=>setSelected('astuces')} style={{
            flex:1,padding:'7px 2px',border:'none',borderRadius:'8px',
            fontSize:'10px',fontWeight:'600',cursor:'pointer',
            backgroundColor:selected==='astuces' ? ASTUCES.color : '#f0f0f0',
            color:selected==='astuces' ? 'white' : '#555',
          }}>💡 Astuces</button>
        </div>

        {/* Contenu selon onglet sélectionné */}
        {selected === 'astuces' ? (
          <div style={{maxHeight:'340px',overflowY:'auto',display:'flex',flexDirection:'column',gap:'8px'}}>
            {ASTUCES.items.map((item, i) => (
              <div key={i} style={{
                padding:'8px 10px',backgroundColor:'#fff8e1',borderRadius:'8px',
                borderLeft:`3px solid ${ASTUCES.color}`,fontSize:'11px',color:'#555',lineHeight:'1.5',
              }}>
                <span style={{color:ASTUCES.color,fontWeight:'700',marginRight:'6px'}}>💡</span>{item}
              </div>
            ))}
          </div>
        ) : (
          <>
        {/* Description */}
        <div style={{
          fontSize:'11px',color:'#555',lineHeight:'1.5',
          backgroundColor:'#f8f9fa',borderRadius:'8px',padding:'8px 10px',
          borderLeft:`3px solid ${shot.color}`,marginBottom:'8px',
        }}>{shot.desc}</div>

        {/* Label étape */}
        <div style={{
          fontSize:'11px',color:shot.color,fontWeight:'600',
          textAlign:'center',minHeight:'28px',lineHeight:'1.4',marginBottom:'6px',
        }}>{label}</div>

        {/* Terrain SVG vertical réaliste */}
        <svg width="100%" viewBox={`0 0 ${W} ${H}`} style={{display:'block',borderRadius:'8px',border:'1px solid #ddd'}}>
          {/* Fond */}
          <rect width={W} height={H} fill="#C4611A" opacity="0.18"/>
          {/* Stries */}
          {Array.from({length:16},(_,i)=>(
            <line key={i} x1={ML} y1={MT+i*18} x2={W-ML} y2={MT+i*18}
              stroke="#9B3D0A" strokeWidth="0.6" opacity="0.3"/>
          ))}

          {/* Contour doubles */}
          <rect x={ML} y={MT} width={W-2*ML} height={H-2*MT}
            fill="none" stroke="white" strokeWidth="1.8"/>
          {/* Lignes de simple */}
          <line x1={CL} y1={MT} x2={CL} y2={H-MT} stroke="white" strokeWidth="1.2"/>
          <line x1={CR} y1={MT} x2={CR} y2={H-MT} stroke="white" strokeWidth="1.2"/>
          {/* Lignes de service */}
          <line x1={CL} y1={SERV_TOP} x2={CR} y2={SERV_TOP} stroke="white" strokeWidth="1.2"/>
          <line x1={CL} y1={SERV_BOT} x2={CR} y2={SERV_BOT} stroke="white" strokeWidth="1.2"/>
          {/* Centrale service */}
          <line x1={W/2} y1={SERV_TOP} x2={W/2} y2={SERV_BOT} stroke="white" strokeWidth="1.2"/>
          {/* Ligne de fond de court J1 (bas) */}
          <line x1={ML} y1={H-MT} x2={W-ML} y2={H-MT} stroke="white" strokeWidth="1.8"/>
          {/* Ligne de fond de court J2 (haut) */}
          <line x1={ML} y1={MT} x2={W-ML} y2={MT} stroke="white" strokeWidth="1.8"/>

          {/* Filet */}
          <rect x={ML-2} y={NET_Y-4} width={W-2*ML+4} height="8" rx="2" fill="white" opacity="0.95"/>
          <rect x={ML-5} y={NET_Y-16} width="5" height="28" rx="1.5" fill="white"/>
          <rect x={W-ML} y={NET_Y-16} width="5" height="28" rx="1.5" fill="white"/>

          {/* Adversaire J2 */}
          <circle cx={W/2} cy={MT+18} r="10" fill="#FF9800"/>
          <text x={W/2} y={MT+22} textAnchor="middle" fontSize="9" fill="white" fontWeight="bold">J2</text>

          {/* Zone cible (étape 3) */}
          {step===3 && (
            <ellipse cx={shot.target.x} cy={shot.target.y} rx="18" ry="10"
              fill={shot.color} opacity="0.25" stroke={shot.color} strokeWidth="1.5"/>
          )}

          {/* Trajectoire balle entrante pointillés */}
          {step>=1 && step<=2 && (
            <line x1={BALL_FROM.x} y1={BALL_FROM.y} x2={BALL_LAND.x} y2={BALL_LAND.y}
              stroke="#FDD835" strokeWidth="2" strokeDasharray="7,4" opacity="0.85"/>
          )}

          {/* Balle */}
          {ballPos && (
            <circle cx={ballPos.x} cy={ballPos.y} r="7"
              fill="#FDD835" stroke="#C8A800" strokeWidth="1.5"/>
          )}

          {/* Trajectoire sortante (étape 3) */}
          {step===3 && (
            <line
              x1={playerX} y1={playerY-14}
              x2={shot.target.x} y2={shot.target.y+10}
              stroke={shot.color} strokeWidth="2.5"
              markerEnd="url(#arrowShot)"/>
          )}

          {/* Flèche déplacement J1 (étape 2) */}
          {step===2 && (
            <line
              x1={J1_START.x-8} y1={J1_START.y}
              x2={J1_MOVED.x+14} y2={J1_MOVED.y}
              stroke="#1976D2" strokeWidth="2" strokeDasharray="5,3"
              markerEnd="url(#arrowMove)"/>
          )}

          {/* Joueur J1 avec translation */}
          <g transform={`translate(${dx},${dy})`}>
            {/* Jambes */}
            <line x1={J1_START.x-7} y1={J1_START.y+3} x2={J1_START.x-11} y2={J1_START.y+18}
              stroke="#1976D2" strokeWidth="5" strokeLinecap="round"/>
            <line x1={J1_START.x+7} y1={J1_START.y+3} x2={J1_START.x+11} y2={J1_START.y+18}
              stroke="#1976D2" strokeWidth="5" strokeLinecap="round"/>
            {/* Corps */}
            <rect x={J1_START.x-9} y={J1_START.y-13} width="18" height="17" rx="4" fill="#1976D2"/>
            {/* Tête */}
            <circle cx={J1_START.x} cy={J1_START.y-22} r="9" fill="#FDBCB4"/>
            {/* Casquette */}
            <rect x={J1_START.x-8} y={J1_START.y-30} width="16" height="6" rx="2" fill="#0D47A1"/>
            <rect x={J1_START.x-12} y={J1_START.y-26} width="8" height="3" rx="1" fill="#0D47A1"/>
            {/* Bras + raquette */}
            {step<3 ? (
              <>
                <line x1={J1_START.x+9} y1={J1_START.y-8}
                  x2={J1_START.x+20} y2={J1_START.y-3}
                  stroke="#FDBCB4" strokeWidth="4" strokeLinecap="round"/>
                <ellipse cx={J1_START.x+27} cy={J1_START.y-9}
                  rx="8" ry="11" fill="none" stroke="#8B4513" strokeWidth="2"
                  transform={`rotate(-20,${J1_START.x+27},${J1_START.y-9})`}/>
                {/* Cordage */}
                <g stroke="#ddd" strokeWidth="0.8" opacity="0.8"
                   transform={`rotate(-20,${J1_START.x+27},${J1_START.y-9})`}>
                  <line x1={J1_START.x+21} y1={J1_START.y-11} x2={J1_START.x+33} y2={J1_START.y-11}/>
                  <line x1={J1_START.x+21} y1={J1_START.y-7} x2={J1_START.x+33} y2={J1_START.y-7}/>
                  <line x1={J1_START.x+27} y1={J1_START.y-18} x2={J1_START.x+27} y2={J1_START.y-0}/>
                </g>
              </>
            ) : (
              <>
                <line x1={J1_START.x+9} y1={J1_START.y-10}
                  x2={J1_START.x+24} y2={J1_START.y-24}
                  stroke="#FDBCB4" strokeWidth="4" strokeLinecap="round"/>
                <ellipse cx={J1_START.x+30} cy={J1_START.y-31}
                  rx="8" ry="11" fill="none" stroke="#8B4513" strokeWidth="2"
                  transform={`rotate(-55,${J1_START.x+30},${J1_START.y-31})`}/>
              </>
            )}
            {/* Label J1 */}
            <text x={J1_START.x} y={J1_START.y+32}
              textAnchor="middle" fontSize="9" fill="#1976D2" fontWeight="bold">J1</text>
          </g>

          <defs>
            <marker id="arrowShot" viewBox="0 0 10 10" refX="8" refY="5"
              markerWidth="5" markerHeight="5" orient="auto-start-reverse">
              <path d="M2 1L8 5L2 9" fill="none" stroke={shot.color} strokeWidth="2" strokeLinecap="round"/>
            </marker>
            <marker id="arrowMove" viewBox="0 0 10 10" refX="8" refY="5"
              markerWidth="5" markerHeight="5" orient="auto-start-reverse">
              <path d="M2 1L8 5L2 9" fill="none" stroke="#1976D2" strokeWidth="2" strokeLinecap="round"/>
            </marker>
          </defs>
        </svg>

        {/* Boutons */}
        <div style={{display:'flex',gap:'8px',marginTop:'10px'}}>
          <button onClick={()=>setStep(0)} style={{
            padding:'8px 12px',border:'1px solid #ddd',borderRadius:'8px',
            backgroundColor:'white',fontSize:'13px',cursor:'pointer',color:'#666',
          }}>↺</button>
          {step<3 ? (
            <button onClick={nextStep} style={{
              flex:1,padding:'8px',border:'none',borderRadius:'8px',
              backgroundColor:shot.color,color:'white',
              fontSize:'12px',fontWeight:'600',cursor:'pointer',
            }}>
              {step===0 ? '▶ Démarrer' : `Étape ${step+1} →`}
            </button>
          ) : (
            <button onClick={onClose} style={{
              flex:1,padding:'8px',border:'none',borderRadius:'8px',
              backgroundColor:shot.color,color:'white',
              fontSize:'12px',fontWeight:'600',cursor:'pointer',
            }}>Compris ✓</button>
          )}
        </div>
        </>
        )}
        {selected === 'astuces' && (
          <button onClick={onClose} style={{
            marginTop:'10px',width:'100%',padding:'8px',border:'none',borderRadius:'8px',
            backgroundColor:ASTUCES.color,color:'white',fontSize:'12px',fontWeight:'600',cursor:'pointer',
          }}>Fermer</button>
        )}
      </div>
    </div>
  );
}